/**
 * Pieces the domain hook modules share: the 404-is-an-answer rule, the optional-query defaults and the
 * create/update/delete-then-invalidate triad. Internal: the index re-exports only `useCrudMutations`.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '../client'

/** Shared defaults for queries where an empty or missing response is expected. */
export const OPTIONAL_QUERY_DEFAULTS = {
  retry: false,
} as const

/**
 * "Nothing here" is an answer; a failure is not. For an endpoint that answers
 * 404 when there is nothing to return, resolve that 404 to `null` and let every
 * other failure stay an error.
 *
 * Three hooks used to catch EVERY error and return null, so a 502 read as "no
 * active plan" and no panel could see the failure at all (Spec 2 D5, #172). One
 * rule, in one place, for every hook where "none" is a real answer.
 */
export async function noneOn404<T>(request: Promise<T>): Promise<T | null> {
  try {
    return await request
  } catch (err) {
    if ((err as { status?: number } | null)?.status === 404) return null
    throw err
  }
}

// ── Shared CRUD mutation triad (Task 9.1, docs/refactor-suggestions.md Phase 9) ──

/**
 * The create/update/delete-then-invalidate triad, repeated identically for
 * events, session templates and coach memories: each mutation hits
 * `basePath` (create), `basePath/{id}` (update/remove), and invalidates the
 * same `queryKey` on success. This only removes that duplicated
 * mutationFn/onSuccess wiring — each entity still gets its own named hooks
 * below (`useCreateEvent` etc.), and every existing call signature is
 * unchanged.
 *
 * `updateMethod` defaults to `'PUT'`; coach memories use `'PATCH'` and pass
 * it explicitly. `TUpdate` defaults to `Partial<TIn>` but callers with a
 * distinct update-shape type (e.g. `TrainingEventUpdate`) can supply it.
 *
 * Not used for `credentials` (a single PUT-keyed upsert with no id/create/
 * delete — see `useUpdateCredential`) or `invites` (create + revoke only, no
 * update leg — see the note above `useCreateInvite`).
 */
export function useCrudMutations<TOut, TIn, TUpdate = Partial<TIn>>(
  basePath: string,
  queryKey: readonly unknown[],
  updateMethod: 'PUT' | 'PATCH' = 'PUT',
) {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: [...queryKey] })

  return {
    create: useMutation({
      mutationFn: (data: TIn) =>
        apiFetch<TOut>(basePath, { method: 'POST', body: JSON.stringify(data) }),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: ({ id, data }: { id: number; data: TUpdate }) =>
        apiFetch<TOut>(`${basePath}/${id}`, { method: updateMethod, body: JSON.stringify(data) }),
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: (id: number) => apiFetch<void>(`${basePath}/${id}`, { method: 'DELETE' }),
      onSuccess: invalidate,
    }),
  }
}
